import type { FC } from 'react';
import React, { Fragment, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Stack from '@atlaskit/primitives/stack';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Text from '@atlaskit/primitives/text';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image/image';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { NTH_USER_AUTO_FOLLOW_SPACES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { onboardingFeedHeaderCard } from '../feedConstants.ts';

import FollowingOnboardingImageDark from './images/FeedOnboardingMessageDark.svg';
import FollowingOnboardingImageLight from './images/FeedOnboardingMessageLight.svg';
import FollowingOnboardingImageDarkV2 from './images/FeedOnboardingMessageV2Dark.svg';
import FollowingOnboardingImageLightV2 from './images/FeedOnboardingMessageV2Light.svg';

export const i18n: {
	headerTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	headerDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	editFeed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	closeMessageLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	headerDescriptionV2: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	customizeFeed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	headerTitle: {
		id: 'feed.header.title',
		defaultMessage: 'We’re keeping you in the loop',
		description: 'Title header from feed onboarding message',
	},
	headerDescription: {
		id: 'feed.header.description',
		defaultMessage:
			'Stay in-the-know by following people and spaces. Their activity will show up in your feed, but you won’t receive email notifications about it. Add to, or edit, your feed anytime.',
		description:
			"Description for feed onboarding message. 'they' being the people followed from the title message",
	},
	editFeed: {
		id: 'feed.header.editFeed',
		defaultMessage: 'Edit feed',
		description: 'Button from feed onboarding message to bring up user settings modal',
	},
	closeMessageLabel: {
		id: 'feed.header.closeLabel',
		defaultMessage: 'Close message box',
		description: 'label for the close icon to dismiss the message box',
	},
	headerDescriptionV2: {
		id: 'feed.header.description.v2',
		defaultMessage:
			'We’ve automatically followed top recommended spaces and people for you, so you never miss important updates. You can add or remove spaces and people from your feed at any time to tailor what you see.',
		description: 'Description for feed onboarding message to explain how feed customization works',
	},
	customizeFeed: {
		id: 'feed.header.customizeFeed',
		defaultMessage: 'Customize feed',
		description: 'Button from feed onboarding message to bring up user settings modal',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BodyContainer = styled.div({
	boxSizing: 'border-box',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	background: token('elevation.surface'),
	display: 'flex',
	marginBottom: token('space.300'),
	padding: `${token('space.300')} ${token('space.300')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageContainer = styled.div({
	flexGrow: 1,
	marginRight: token('space.250'),
	marginBottom: token('space.300'),
});

const descriptionTextStyles = xcss({
	marginBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OnBoardingImage = styled(Image)({
	maxWidth: '200px',
	maxHeight: '160px',
	flexShrink: 0,
	alignSelf: 'flex-end',
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButtonContainer = styled.div({
	flexShrink: 0,
});

type FeedOnboardingMessageProps = {
	openActivityFilterModal: () => void;
	servingRecommendations: boolean;
} & WrappedComponentProps;

const defaultContentConfig = {
	lightImage: FollowingOnboardingImageLight,
	darkImage: FollowingOnboardingImageDark,
	description: (
		<Text size="large" weight="regular">
			<FormattedMessage {...i18n.headerDescription} />
		</Text>
	),
	button: <FormattedMessage {...i18n.editFeed} />,
	title: (
		<Heading size="small" as="h3">
			<Text size="large" weight="regular">
				<FormattedMessage {...i18n.headerTitle} />
			</Text>
		</Heading>
	),
};

const autoFollowContentConfig = {
	lightImage: FollowingOnboardingImageLightV2,
	darkImage: FollowingOnboardingImageDarkV2,
	description: (
		<Text size="large" weight="regular" color="color.text.subtle">
			<FormattedMessage {...i18n.headerDescriptionV2} />
		</Text>
	),
	button: (
		<Text color="color.text.subtle">
			<FormattedMessage {...i18n.customizeFeed} />
		</Text>
	),
	title: (
		<Heading size="small">
			<FormattedMessage {...i18n.headerTitle} />
		</Heading>
	),
};

const FeedOnboardingMessageComponent: FC<FeedOnboardingMessageProps> = ({
	intl,
	openActivityFilterModal,
	servingRecommendations,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [showFeature, stop, featureDiscoveryError] = useFeatureDiscovery(onboardingFeedHeaderCard);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(NTH_USER_AUTO_FOLLOW_SPACES.HAS_AUTO_FOLLOWED_SPACES);

	const { hasAutoFollowedSpaces } = deserializeState(onboardingStateData);

	const contentConfig = useMemo(() => {
		if (hasAutoFollowedSpaces) {
			// > 10 users
			return autoFollowContentConfig;
		}
		return defaultContentConfig;
	}, [hasAutoFollowedSpaces]);

	const error = featureDiscoveryError || onboardingStateError;

	useEffect(() => {
		if (
			(servingRecommendations || Boolean(hasAutoFollowedSpaces)) &&
			showFeature &&
			!error &&
			!onboardingStateLoading
		) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'feedOnboardingMessage',
					attributes: {
						isAutoFollowContent: hasAutoFollowedSpaces,
					},
				},
			}).fire();
		}
	}, [
		createAnalyticsEvent,
		error,
		showFeature,
		servingRecommendations,
		onboardingStateLoading,
		hasAutoFollowedSpaces,
	]);

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	if (
		onboardingStateLoading ||
		!showFeature ||
		// When spaces are auto-followed, servingRecommendations will be set to false
		// This is a workaround to ignore the value if the follow is not user-initiated
		(!servingRecommendations && !hasAutoFollowedSpaces)
	) {
		return null;
	}

	const handleClick = () => {
		openActivityFilterModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'feedOnboardingUpdateFeed',
				source: 'feedOnboardingMessage',
				attributes: {
					isAutoFollowContent: Boolean(hasAutoFollowedSpaces),
				},
			},
		}).fire();
	};

	const handleDismiss = () => {
		stop();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'feedOnboardingDismiss',
				source: 'feedOnboardingMessage',
				attributes: {
					isAutoFollowContent: Boolean(hasAutoFollowedSpaces),
				},
			},
		}).fire();
	};

	return (
		<BodyContainer data-testid="feed-onboarding-message" data-vc="feed-onboarding-message">
			<MessageContainer>
				<Stack space="space.100">
					{contentConfig.title}
					<Box xcss={descriptionTextStyles}>{contentConfig.description}</Box>
				</Stack>
				<Fragment>
					<Button onClick={handleClick}>{contentConfig.button}</Button>
				</Fragment>
			</MessageContainer>

			<OnBoardingImage
				src={contentConfig.lightImage}
				srcDark={contentConfig.darkImage}
				width="200"
				height="160"
				alt=""
				data-vc="following-onboarding-image"
			/>

			<CloseButtonContainer>
				<IconButton
					onClick={handleDismiss}
					spacing="compact"
					appearance="subtle"
					icon={CrossIcon}
					label={intl.formatMessage(i18n.closeMessageLabel)}
					isTooltipDisabled={false}
				/>
			</CloseButtonContainer>
		</BodyContainer>
	);
};

export const FeedOnboardingMessage: FC<WithIntlProps<FeedOnboardingMessageProps>> & {
	WrappedComponent: React.ComponentType<FeedOnboardingMessageProps>;
} = injectIntl(FeedOnboardingMessageComponent);
